import React, { useState, useRef, useEffect } from "react";
import axios from 'axios';
import "../../css/dashboard.css";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";

function ASDetail(props) {
    const location = useLocation();
    const navigate = useNavigate();

    const [asList, setASList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [imagePaths, setImagePaths] = useState([]);
    const [viewType, setViewType] = useState(null);
    const [enlargedImage, setEnlargedImage] = useState(null);
    const imageModalRef = useRef();

    const startBeforePage = () => {
        if (location.state && location.state.beforePage) {
            navigate(location.state.beforePage);
        } else {
            navigate(-1);
        }
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setASList(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const putASInfo = async () => {
        const requestData = {
            advisor: asList.advisor,
            advisor_content: asList.advisor_content,
        };
        try {
            const put_result = await axios.put(`/ASRegist/${asList.idx}`, requestData);
            if (put_result.data.success === true) {
                alert('처리가 완료되었습니다');
                startBeforePage();
            } else {
                alert('처리에 실패하였습니다');
            }
        } catch (error) {
            console.error('Error :', error.message);
        }
    };

    useEffect(() => {
        const getReservationDetail = async () => {
            try {
                const as_result = await axios.get(`/ASRegistIdx/${location.state.idx}`);
                console.log("AS Data:", as_result.data[0]); // ✅ 여기서 데이터 구조 확인!
                setASList(as_result.data[0]);
            } catch (error) {
                console.error('Error fetching reservation count:', error.message);
            }
        };
        getReservationDetail();
    }, [location.state.idx]);

    const showCustomerImages = async () => {
        setViewType('CUSTOMER');
        setImagePaths([]);
        setIsLoading(true);
        imageModalRef.current.showModal();

        try {
            const userId = encodeURIComponent(asList.id);
            const reqNumber = encodeURIComponent(asList.req_number);
            const response = await axios.get(`/imagesName/user/reservation/${userId}/${reqNumber}`);

            if (response.data.success) {
                const imageFiles = response.data.images;
                const imagePromises = imageFiles.map(async (imageName) => {
                    const imageResponse = await axios.get(`/imagesRead/user/reservation/${userId}/${reqNumber}/${imageName}`, {
                        responseType: 'blob'
                    });
                    return URL.createObjectURL(imageResponse.data);
                });
                const imageUrls = await Promise.all(imagePromises);
                setImagePaths(imageUrls);
            }
        } catch (error) {
            console.error('고객 이미지 가져오기 오류:', error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const showManagerImages = async () => {
        setViewType('MANAGER');
        setImagePaths([]);
        setIsLoading(true);
        imageModalRef.current.showModal();

        try {
            const userId = encodeURIComponent(asList.id);
            const reqNumber = encodeURIComponent(asList.req_number);
            const response = await axios.get(`/imagesName/as_complete/${userId}/${reqNumber}`);

            if (response.data.success) {
                const imageFiles = response.data.images;
                const imagePromises = imageFiles.map(async (imageName) => {
                    const imageResponse = await axios.get(`/imagesRead/as_complete/${userId}/${reqNumber}/${imageName}`, {
                        responseType: 'blob'
                    });
                    return URL.createObjectURL(imageResponse.data);
                });
                const imageUrls = await Promise.all(imagePromises);
                setImagePaths(imageUrls);
            }
        } catch (error) {
            console.error('매니저 이미지 가져오기 오류:', error.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <PageContainer>
            <Card>
                <CardHeader>
                    <Title>A/S 상세 정보</Title>
                </CardHeader>

                <SectionTitle>기본 정보</SectionTitle>
                <GridContainer>
                    <InfoItem label="AS번호" value={asList?.as_number || '-'} />
                    <InfoItem label="예약번호" value={asList?.req_number} />
                    <InfoItem label="세척일자" value={asList?.work_date} />
                    <InfoItem label="고객명" value={asList?.name} />
                    <InfoItem label="아이디" value={asList?.id} />
                    <InfoItem label="연락처" value={asList?.phone_num} />
                    <InfoItem label="주소" value={asList?.address} span={2} />
                </GridContainer>

                <SectionTitle>작업 및 A/S 내용</SectionTitle>
                <GridContainer>
                    <InfoItem label="에어컨 종류" value={asList?.aircon_kind} />
                    <InfoItem label="수량" value={asList?.aircon_count} />
                    <InfoItem label="A/S 내용" value={asList?.as_content} span={2} />
                    <InfoItem label="AS 상세내용 (1000자 이내)" value={asList?.description || asList?.as_detail || '-'} span={4} isTextArea />

                    <div style={{ gridColumn: 'span 2' }}>
                        <Label>고객 첨부 사진</Label>
                        <SecondaryButton onClick={showCustomerImages} style={{ marginTop: '5px', width: '100%' }}>사진 보기</SecondaryButton>
                    </div>
                    <div style={{ gridColumn: 'span 2' }}>
                        <Label>매니저 완료 사진</Label>
                        <SecondaryButton onClick={showManagerImages} style={{ marginTop: '5px', width: '100%' }}>사진 보기</SecondaryButton>
                    </div>
                </GridContainer>

                <SectionTitle>담당자 및 처리 결과</SectionTitle>
                <GridContainer>
                    <InfoItem label="담당기사" value={asList?.manager_id} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <Label>담당 상담원</Label>
                        <StyledInput
                            name="advisor"
                            value={asList?.advisor || ''}
                            onChange={handleInputChange}
                            placeholder="상담원 이름 입력"
                        />
                    </div>
                    <div style={{ gridColumn: 'span 4', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <Label>상담 내용 (1000자 이내)</Label>
                        <StyledTextArea
                            name="advisor_content"
                            value={asList?.advisor_content || ''}
                            onChange={handleInputChange}
                            placeholder="처리 결과 및 상담 내용을 입력하세요."
                        />
                    </div>
                </GridContainer>

                <ButtonGroup>
                    <OutlineButton onClick={startBeforePage}>목록으로</OutlineButton>
                </ButtonGroup>
            </Card>

            {/* 이미지 모달 */}
            <StyledDialog ref={imageModalRef}>
                <DialogHeader>
                    <h3>{viewType === 'MANAGER' ? '작업 전/후 사진' : '고객 첨부 사진'}</h3>
                </DialogHeader>

                <DialogContent>
                    {isLoading ? (
                        <LoadingBox>
                            <div className="spinner"></div>
                            <p>이미지를 불러오고 있습니다...</p>
                        </LoadingBox>
                    ) : (
                        <>
                            {imagePaths.length === 0 ? (
                                <EmptyBox>등록된 사진이 없습니다.</EmptyBox>
                            ) : (
                                viewType === 'MANAGER' ? (
                                    <ManagerImageGrid>
                                        <ImageColumn title="작업 전">
                                            {imagePaths[0] && <StyledImage src={imagePaths[0]} alt="Before 1" onClick={() => setEnlargedImage(imagePaths[0])} />}
                                            {imagePaths[2] && <StyledImage src={imagePaths[2]} alt="Before 2" onClick={() => setEnlargedImage(imagePaths[2])} />}
                                        </ImageColumn>
                                        <Divider />
                                        <ImageColumn title="작업 후">
                                            {imagePaths[1] && <StyledImage src={imagePaths[1]} alt="After 1" onClick={() => setEnlargedImage(imagePaths[1])} />}
                                            {imagePaths[3] && <StyledImage src={imagePaths[3]} alt="After 2" onClick={() => setEnlargedImage(imagePaths[3])} />}
                                        </ImageColumn>
                                    </ManagerImageGrid>
                                ) : (
                                    <CustomerImageScroll>
                                        {imagePaths.map((url, idx) => (
                                            <StyledImage
                                                key={idx}
                                                src={url}
                                                alt={`Customer ${idx}`}
                                                style={{ height: '300px', width: 'auto' }}
                                                onClick={() => setEnlargedImage(url)}
                                            />
                                        ))}
                                    </CustomerImageScroll>
                                )
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <PrimaryButton onClick={() => imageModalRef.current.close()}>닫기</PrimaryButton>
                </DialogActions>

                {/* 이미지 확대 오버레이 - 모달 내부에 배치 */}
                {enlargedImage && (
                    <ImageOverlay onClick={() => setEnlargedImage(null)}>
                        <EnlargedImageWrapper onClick={(e) => e.stopPropagation()}>
                            <EnlargedImage src={enlargedImage} alt="확대 이미지" />
                            <CloseOverlayButton onClick={() => setEnlargedImage(null)}>×</CloseOverlayButton>
                        </EnlargedImageWrapper>
                    </ImageOverlay>
                )}
            </StyledDialog>
        </PageContainer>
    )
}

// --- Helper Components ---
const InfoItem = ({ label, value, span = 1, isTextArea, isHighlight }) => (
    <div style={{ gridColumn: `span ${span}`, display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <Label>{label}</Label>
        {isTextArea ? (
            // ✅ div 태그 사이에 값을 넣어야 합니다 (children 방식)
            <ReadOnlyTextArea>{value || ''}</ReadOnlyTextArea>
        ) : (
            <Value isHighlight={isHighlight}>{value || '-'}</Value>
        )}
    </div>
);

const ImageColumn = ({ title, children }) => (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'center' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '5px', color: '#555' }}>{title}</div>
        {children}
    </div>
);

// --- Styled Components ---
// --- Styled Components (80% Compact Version) ---

const PageContainer = styled.div`
    padding: 20px; // 30px -> 20px
    background-color: #f5f6f8;
    min-height: 100vh;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding-top: 40px; // 50px -> 40px
    box-sizing: border-box;
`;

const Card = styled.div`
    background: white;
    width: 100%;
    max-width: 1000px;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    padding: 32px 36px; // 40px 45px -> 32px 36px (80%)
    display: flex;
    flex-direction: column;
    gap: 20px; // 25px -> 20px (80%)
`;

const CardHeader = styled.div`
    border-bottom: 1px solid #eee;
    padding-bottom: 12px; // 15px -> 12px
    margin-bottom: 8px; // 10px -> 8px
`;

const Title = styled.h2`
    font-size: 1.5rem; // 1.6rem -> 1.5rem (미세 축소)
    color: #111;
    margin: 0;
    font-weight: 800;
`;

const SectionTitle = styled.h3`
    font-size: 1.1rem; // 1.15rem -> 1.1rem
    color: #333;
    margin: 0 0 10px 0; // 12px -> 10px
    border-left: 4px solid #006edc;
    padding-left: 10px; // 12px -> 10px
`;

const GridContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    column-gap: 20px; // 25px -> 20px
    row-gap: 16px; // 20px -> 16px (80%)
    
    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const Label = styled.div`
    font-size: 0.8rem; // 0.85rem -> 0.8rem
    color: #888;
    font-weight: 500;
    margin-bottom: 3px; // 4px -> 3px
`;

const Value = styled.div`
    font-size: 0.95rem; // 1rem -> 0.95rem
    color: ${props => props.isHighlight ? '#d32f2f' : '#222'};
    font-weight: ${props => props.isHighlight ? 'bold' : 'normal'};
    border-bottom: 1px solid #f0f0f0;
    padding-bottom: 5px; // 6px -> 5px
    min-height: 22px; // 24px -> 22px
    word-break: break-all;
`;

const ReadOnlyTextArea = styled.div`
    background-color: #f9f9f9;
    padding: 8px 10px; // 10px -> 8px 10px
    border-radius: 6px;
    border: 1px solid #eee;
    min-height: 50px; // 60px -> 50px
    font-size: 0.9rem;
    line-height: 1.4; // 1.5 -> 1.4
    color: #444;
    white-space: pre-wrap;
`;

const StyledInput = styled.input`
    width: 100%;
    padding: 8px 10px; // 10px 12px -> 8px 10px
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.95rem;
    box-sizing: border-box;
    &:focus { border-color: #006edc; outline: none; }
`;

const StyledTextArea = styled.textarea`
    width: 100%;
    height: 80px; // 100px -> 80px (높이 줄임)
    padding: 8px 10px; // 패딩 줄임
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.95rem;
    resize: vertical;
    box-sizing: border-box;
    font-family: inherit;
    &:focus { border-color: #006edc; outline: none; }
`;

const ButtonGroup = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px; // 25px -> 20px
    padding-top: 20px; // 25px -> 20px
    border-top: 1px solid #eee;
`;

const BaseButton = styled.button`
    padding: 8px 18px; // 10px 22px -> 8px 18px
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
    font-size: 0.9rem; // 0.95rem -> 0.9rem
`;

// ... (PrimaryButton, SecondaryButton, OutlineButton 등은 기존과 동일하므로 생략 가능하지만, 안전하게 복사해서 쓰세요)
const PrimaryButton = styled(BaseButton)`
    background-color: #006edc;
    color: white;
    &:hover { background-color: #0056b3; }
`;

const SecondaryButton = styled(BaseButton)`
    background-color: #f0f0f0;
    color: #333;
    &:hover { background-color: #e0e0e0; }
`;

const OutlineButton = styled(BaseButton)`
    background-color: white;
    border: 1px solid #ccc;
    color: #555;
    &:hover { background-color: #f9f9f9; border-color: #bbb; }
`;

// --- Modal Styles (그대로 유지하되 패딩만 살짝 줄임) ---
const StyledDialog = styled.dialog`
    border: none;
    border-radius: 16px;
    padding: 0;
    width: 90%;
    max-width: 1000px;
    box-shadow: 0 10px 40px rgba(0,0,0,0.25);
    &::backdrop { background: rgba(0, 0, 0, 0.6); }
`;

const DialogHeader = styled.div`
    padding: 16px 20px; // 20px 25px -> 16px 20px
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
    h3 { margin: 0; font-size: 1.2rem; font-weight: 700; }
`;

const DialogContent = styled.div`
    padding: 24px; // 30px -> 24px
    background: #fff;
    min-height: 300px;
    max-height: 80vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
`;

const DialogActions = styled.div`
    padding: 16px 20px; // 20px 25px -> 16px 20px
    border-top: 1px solid #eee;
    display: flex;
    justify-content: center;
`;

// ... (나머지 Helper Components와 LoadingBox 등은 기존과 동일)
const CloseButton = styled.button`
    background: none; border: none; font-size: 1.8rem; cursor: pointer; color: #999; line-height: 1;
    &:hover { color: #333; }
`;

const LoadingBox = styled.div`
    flex: 1; display: flex; flexDirection: column; justify-content: center; align-items: center;
    .spinner {
        width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid #3498db;
        border-radius: 50%; animation: spin 1s linear infinite;
    }
    @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
`;

const EmptyBox = styled.div`
    flex: 1; display: flex; justify-content: center; align-items: center; color: #999; font-size: 1.1rem;
`;

const ManagerImageGrid = styled.div`
    display: flex; gap: 20px; flex: 1;
`;

const Divider = styled.div`
    width: 1px; background-color: #eee;
`;

const CustomerImageScroll = styled.div`
    display: flex; gap: 15px; overflow-x: auto; padding-bottom: 10px;
`;

const StyledImage = styled.img`
    width: 100%; max-height: 300px; object-fit: contain; border: 1px solid #eee; border-radius: 8px;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    &:hover {
        transform: scale(1.02);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
`;

const ImageOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.9);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10000;
    cursor: pointer;
`;

const EnlargedImageWrapper = styled.div`
    position: relative;
    max-width: 95vw;
    max-height: 95vh;
`;

const EnlargedImage = styled.img`
    max-width: 95vw;
    max-height: 95vh;
    object-fit: contain;
    border-radius: 8px;
    cursor: default;
`;

const CloseOverlayButton = styled.button`
    position: absolute;
    top: -40px;
    right: 0;
    background: none;
    border: none;
    color: white;
    font-size: 2.5rem;
    cursor: pointer;
    line-height: 1;
    &:hover {
        color: #ccc;
    }
`;

export default ASDetail;